interface EcommerceItem {
  item_id: string;
  item_name: string;
  item_category: string;
  item_brand: string;
  price: number;
  quantity: number;
}

interface EcommerceData {
  currency: string;
  value: number;
  items: EcommerceItem[];
  transaction_id?: string;
  shipping?: number;
  tax?: number;
  coupon?: string;
}

interface MetaPixelData {
  content_type: string;
  content_ids: string[];
  content_name?: string;
  content_category?: string;
  value: number;
  currency: string;
  // Enhanced catalog fields
  brand?: string;
  availability?: string;
  condition?: string;
  description?: string;
  image_link?: string;
  link?: string;
  retailer_item_id?: string;
  google_product_category?: string;
}

class AnalyticsService {
  private pushToDataLayer(event: string, data: any) {
    if (typeof window !== "undefined" && window.dataLayer) {
      console.log(`📊 Analytics Event: ${event}`, data);
      window.dataLayer.push({
        event,
        ...data,
      });
    }
  }

  // Product View Tracking
  trackProductView(product: any) {
    // Extract proper price value
    const price =
      product.price?.discount?.newPrice ||
      product.price?.regPrice ||
      product.price ||
      0;

    const ecommerce: EcommerceData = {
      currency: "PEN",
      value: price,
      items: [
        {
          item_id: product.sku || product.id.toString(),
          item_name: product.name,
          item_category: product.category?.name || "General",
          item_brand: product.partner?.name || "Evefant",
          price: price,
          quantity: 1,
        },
      ],
    };

    const meta_pixel: MetaPixelData = {
      content_type: "product",
      content_ids: [product.sku || product.id.toString()],
      content_name: product.name,
      content_category: product.category?.name || "General",
      value: price,
      currency: "PEN",
      // Enhanced catalog data
      brand: product.partner?.name || "Evefant",
      availability: product.stock > 0 ? "in stock" : "out of stock",
      condition: "new",
      description: product.description?.substring(0, 255) || product.name,
      image_link: product.images?.[0]?.medium || product.images?.[0]?.original,
      link: `https://evefant.com${window.location.pathname}`, // Use current page URL
      retailer_item_id: product.sku || product.id.toString(),
      google_product_category: this.mapCategoryToGoogle(product.category?.name),
    };

    this.pushToDataLayer("view_item", { ecommerce, meta_pixel });
  }

  // Helper method to map categories to Google product categories
  private mapCategoryToGoogle(category?: string): string {
    const categoryMap: { [key: string]: string } = {
      Globos: "Toys & Games > Party Supplies > Balloons",
      Regalos: "Gifts & Special Event Items",
      Flores: "Home & Garden > Plants > Flowers",
      Chocolates: "Food, Beverages & Tobacco > Food Items > Snack Foods",
      Peluches: "Toys & Games > Toys > Stuffed Animals",
      General: "General Merchandise",
    };

    return categoryMap[category || "General"] || "General Merchandise";
  }

  // Add to Cart Tracking
  trackAddToCart(product: any, quantity: number = 1, totalCartValue: number) {
    // Extract proper price value
    const price =
      product.price?.discount?.newPrice ||
      product.price?.regPrice ||
      product.price ||
      0;

    const ecommerce: EcommerceData = {
      currency: "PEN",
      value: totalCartValue,
      items: [
        {
          item_id: product.sku || product.id.toString(),
          item_name: product.name,
          item_category: product.category?.name || "General",
          item_brand: product.partner?.name || "Evefant",
          price: price,
          quantity: quantity,
        },
      ],
    };

    const meta_pixel: MetaPixelData = {
      content_type: "product",
      content_ids: [product.sku || product.id.toString()],
      content_name: product.name,
      content_category: product.category?.name || "General",
      value: totalCartValue,
      currency: "PEN",
      // Enhanced catalog data for add to cart
      brand: product.partner?.name || "Evefant",
      availability: product.stock > 0 ? "in stock" : "out of stock",
      condition: "new",
      description: product.description?.substring(0, 255) || product.name,
      image_link: product.images?.[0]?.medium || product.images?.[0]?.original,
      link: `https://evefant.com${window.location.pathname}`, // Use current page URL
      retailer_item_id: product.sku || product.id.toString(),
    };

    this.pushToDataLayer("add_to_cart", { ecommerce, meta_pixel });
  }

  // Begin Checkout Tracking
  trackBeginCheckout(cartItems: any[], totalValue: number, coupon?: string) {
    const items: EcommerceItem[] = cartItems.map((item) => ({
      item_id: item.sku || item.id.toString(),
      item_name: item.name,
      item_category: item.category || "General",
      item_brand: item.partner?.name || "Evefant",
      price: item.price || 0,
      quantity: item.quantity || 1,
    }));

    const ecommerce: EcommerceData = {
      currency: "PEN",
      value: totalValue,
      items,
      ...(coupon && { coupon }),
    };

    const meta_pixel: MetaPixelData = {
      content_type: "product",
      content_ids: items.map((item) => item.item_id),
      value: totalValue,
      currency: "PEN",
    };

    this.pushToDataLayer("begin_checkout", { ecommerce, meta_pixel });
  }

  // Purchase Tracking
  trackPurchase(orderData: any) {
    const items: EcommerceItem[] = orderData.items.map((item: any) => ({
      item_id: item.sku || item.id.toString(),
      item_name: item.name,
      item_category: item.category || "General",
      item_brand: item.partner?.name || "Evefant",
      price: item.price || 0,
      quantity: item.quantity || 1,
    }));

    const ecommerce: EcommerceData = {
      transaction_id: orderData.id,
      currency: "PEN",
      value: orderData.total,
      shipping: orderData.shipping || 0,
      tax: orderData.tax || 0,
      items,
      ...(orderData.coupon && { coupon: orderData.coupon }),
    };

    const meta_pixel: MetaPixelData = {
      content_type: "product",
      content_ids: items.map((item) => item.item_id),
      value: orderData.total,
      currency: "PEN",
    };

    this.pushToDataLayer("purchase", { ecommerce, meta_pixel });
  }

  // Search Tracking
  trackSearch(searchTerm: string) {
    this.pushToDataLayer("search", { search_term: searchTerm });
  }

  // Newsletter Subscribe Tracking
  trackNewsletterSubscribe(email: string, type: string = "general") {
    this.pushToDataLayer("newsletter_subscribe", {
      newsletter_email: email,
      newsletter_type: type,
    });
  }

  // Form Submission Tracking
  trackFormSubmit(formName: string, formId?: string) {
    this.pushToDataLayer("form_submit", {
      form_name: formName,
      ...(formId && { form_id: formId }),
    });
  }

  // Product Customization Tracking
  trackProductCustomization(
    product: any,
    customizationType: string,
    customizationValue: string,
    additionalPrice: number = 0
  ) {
    this.pushToDataLayer("product_customize", {
      customization_type: customizationType,
      customization_value: customizationValue,
      customization_price: additionalPrice,
      // Include product context
      ecommerce: {
        currency: "PEN",
        value: product.price + additionalPrice,
        items: [
          {
            item_id: product.sku || product.id.toString(),
            item_name: product.name,
            item_category: product.category || "General",
            item_brand: product.partner?.name || "Evefant",
            price: product.price + additionalPrice,
            quantity: 1,
          },
        ],
      },
    });
  }
}

export const analyticsService = new AnalyticsService();
